import * as Sentry from '@sentry/react'
import { Dayjs } from 'dayjs'
import stringify from 'fast-safe-stringify'
import { PathsWithMethod } from 'openapi-typescript-helpers'

import { add, DATE_TEMPLATES, day, format, getToday, isAfter, isBefore } from '~/astra'
import { getClient } from '~/clients/dips-client'
import { paths } from '~/clients/dips-types.generated'
import env from '~/env'
import { getFlag, getStringFlag } from '~/hooks/useFlag'
import { selectSections as selectStormSections } from '~/storm'
import { isNotNullish } from '~/utils/guards'

import { getErrorMessage } from './middleware/utils'
import { DepartmentKey, SelectedSpecialties } from './slices/filterSlice'
import { useStore } from './store'
import { transformDipsLocalizations } from './transformers/dipsLocalizationTransformer'
import { DipsPostponementReason, transformDipsPostponementReasons } from './transformers/dipsPostponementReasonsTransformer'
import { transformDipsResources } from './transformers/dipsResourceTransformer'
import { transformDipsWards } from './transformers/dipsWardTransformer'
import { transformScheduledSurgeries, UnifiedScheduledSurgery } from './transformers/scheduledSurgeryTransformer'
import { transformUnScheduledSurgeries } from './transformers/unScheduledSurgery/unScheduledSurgeryTransformer'
import { withFetchObserver } from './utils/fetchObserver'

type WithValidityDates = { validFrom?: string | null; validTo?: string | null }

const filterByValidity = <T extends WithValidityDates>(items: T[]): T[] => {
    const today = getToday()
    return items
        .filter(item => item.validTo === null || isAfter(day(item.validTo), today))
        .filter(item => item.validFrom === null || isBefore(day(item.validFrom), today))
}

export const importFullScheduledSurgeries = withFetchObserver(
    async function (startTime: string, endTime: string, treatmentLocationId: number, id?: string): Promise<UnifiedScheduledSurgery[]> {
        const hasId = id !== undefined

        const apiVersion = getStringFlag('dips__scheduled_surgeries_api_version')

        const path = `/api/${apiVersion}/surgery/scheduled` satisfies PathsWithMethod<paths, 'get'>
        const params = {
            params: {
                query: {
                    ...(hasId && { BookingId: id }),
                    TreatmentLocationId: treatmentLocationId,
                    StartTime: startTime,
                    EndTime: endTime,
                    View: 'full',
                },
            },
        }

        const client = getClient()
        let response

        try {
            response = await client.GET(path, params)
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`${getErrorMessage(path, params, error.message)}`)
            } else {
                throw new Error(`Unknown error. ${getErrorMessage(path, params, error)}`)
            }
        }

        if (!String(response.response.status).startsWith('2')) {
            throw new Error(`${getErrorMessage(path, params, response)}`)
        }

        const responseData = response.data
        if (!responseData) {
            console.error(`No data in ${path} using params: ${stringify({ startTime, endTime, treatmentLocationId, ...(hasId && { bookingId: id }) })}`)
            return []
        }

        const transformed = transformScheduledSurgeries(responseData)

        useStore.getState().dips.actions.addFullScheduledSurgeries(transformed)

        return transformed
    },
    'id',
    {
        abandoned: ids => {
            useStore.getState().dips.actions.removeFullScheduledSurgeries(ids)
        },
    }
)

/** Fetch one booking, over a range spanning `surgeryDate` up to but not including the next day. */
export const importFullScheduledSurgery = (bookingId: string, surgeryDate: Dayjs) => {
    const startTime = format(surgeryDate, DATE_TEMPLATES.iso)
    const endTime = format(add(surgeryDate, 1, 'day'), DATE_TEMPLATES.iso)
    const treatmentLocationId = Number(env.VITE_DIPS_TREATMENT_LOCATION_ID)

    return importFullScheduledSurgeries(startTime, endTime, treatmentLocationId, bookingId)
}

/**
 * Builds the unscheduled-surgeries POST body from a set of DIPS sections.
 * @param selectedSections - The Hero sections whose DIPS IDs drive the POST parameters.
 * @returns An object containing the body of the POST request with treatment location ID, department IDs, section IDs, ward IDs, and localization IDs.
 */
const buildPostParamsFromSections = (selectedSections: ReturnType<typeof selectStormSections>) => {
    // The POST endpoint performs an AND operation on the different IDs
    // NOTE: null means "blank", and this is required to include surgeries with missing IDs (which is a commmon case except for departmentIds)

    const state = useStore.getState()

    // Filter out sections with nullish DIPS section ID to prevent duplicate null values in sectionIds
    const filteredSections = selectedSections.filter(section => isNotNullish(section.dips_section_id))

    // NOTE: Using filteredSections to map departmentIds excludes surgeries with null section ID for departments that are not selected
    // LDS has one department (always selected), but multi-department hospitals need all departments included to not exclude any surgeries
    // Since there might be some performance issues with multi-department queries, we wait with implementing this as it may require separate requests per department

    // Remove duplicate department IDs as some sections may belong to the same DIPS department
    const departmentIds = [...new Set(filteredSections.map(section => section.dips_department_id))]

    const sectionIds = [null, ...filteredSections.map(section => section.dips_section_id)]

    function isInSelectedDepartmentIds<T extends { departmentIds?: number[] | null }>(item: T): boolean {
        if (departmentIds.length > 0 && isNotNullish(item.departmentIds) && item.departmentIds.length > 0) {
            return item.departmentIds.some(itemDepartmentId => departmentIds.includes(itemDepartmentId))
        }
        return true
    }

    const wardIds = [
        null,
        ...Object.values(state.dips.entities.dipsWards.byId)
            .filter(isInSelectedDepartmentIds)
            .map(ward => ward.id),
    ]

    const localizationIds = [
        null,
        ...Object.values(state.dips.entities.dipsLocalizations.byId)
            .filter(isInSelectedDepartmentIds)
            .map(localization => localization.id),
    ]

    return {
        body: {
            treatmentLocationId: Number(env.VITE_DIPS_TREATMENT_LOCATION_ID),
            departmentIds,
            sectionIds,
            wardIds,
            locationIds: localizationIds, // DIPS have a typo for the localization endpoint (should be locations)
        },
    }
}

/**
 * Creates the POST parameters for fetching unscheduled surgeries based on the selected department in Hero.
 * @param departmentKey - The key of the Hero department for which to create the POST parameters.
 * @returns An object containing the body of the POST request with treatment location ID, department IDs, section IDs, ward IDs, and localization IDs.
 */
const createPostParams = (departmentKey: DepartmentKey) => {
    // The Hero departments are in fact groups of DIPS sections, so we try to use departmentIds, localizationIds, and wardIds based on the selected sections
    const state = useStore.getState()

    const selectedSections = departmentKey === 'all' ? selectStormSections(state) : selectStormSections(state, { where: { department_id: departmentKey } })

    return buildPostParamsFromSections(selectedSections)
}

/**
 * Creates the POST parameters for fetching unscheduled surgeries based on the selected specialties in Hero.
 * @param selectedSpecialties - The UUIDs of the Hero specialties for which to create the POST parameters. `'all'` selects all sections.
 * @returns An object containing the body of the POST request with treatment location ID, department IDs, section IDs, ward IDs, and localization IDs.
 */
export const createPostParamsFromSpecialties = (selectedSpecialties: SelectedSpecialties) => {
    // The Hero specialties map to groups of DIPS sections, so we derive departmentIds, localizationIds, and wardIds based on the selected sections
    const state = useStore.getState()

    const selectedSections =
        selectedSpecialties === 'all' ? selectStormSections(state) : selectStormSections(state, { where: { speciality_uuid: { in: selectedSpecialties } } })

    return buildPostParamsFromSections(selectedSections)
}

let hasShadowed = false
/**
 * This is a temporary function to test what happens when the v6 API is used.
 * Should be removed after testing. Testing date is likely 14. Sept 2026.
 * If you see this code after that date, feel free to delete it.
 * Context: https://deepinsight-no.slack.com/archives/C0BUPGDJ8J2/p1789110053920399
 */
async function shadowFetchUnscheduledV6(params: ReturnType<typeof buildPostParamsFromSections>): Promise<void> {
    const apiVersion = getStringFlag('dips__un_scheduled_surgeries_api_version')
    const shouldShadow = getFlag('waiting_list__shadow_v6_test')

    if (apiVersion !== 'v3' || !shouldShadow || hasShadowed) {
        return
    }

    hasShadowed = true

    await Sentry.startSpan(
        { name: 'shadowWaitingListV6', op: 'shadow.request', attributes: { 'shadow.api': 'v6', 'shadow.source': 'v3-ondemand' } },
        async span => {
            const startedAt = Date.now()
            try {
                const client = getClient()
                const res = await client.POST('/api/v6/surgeries/waiting', params)

                if (!res.response.ok) {
                    throw new Error(`[HTTP ${res.response.status}] shadow v6 failed: res.response.ok = ${res.response.ok}`)
                }

                const entities = res.data?.surgeryWaitingList ?? []

                // This checks that the data is parseable by the transformer. Throws if not.
                transformUnScheduledSurgeries(entities)

                span.setAttribute('shadow.success', true)
                span.setAttribute('shadow.count', entities.length)
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { shadow: 'waitinglist-v6' },
                    extra: { durationMs: Date.now() - startedAt, at: new Date().toISOString() },
                })
            }
        }
    )
}

const fetchUnScheduledSurgeries = async (params: ReturnType<typeof buildPostParamsFromSections>) => {
    // This is a temporary test - contact Amiran or Jostein if you have questions.
    // Context: https://deepinsight-no.slack.com/archives/C0BUPGDJ8J2/p1789110053920399
    void shadowFetchUnscheduledV6(params)

    const apiVersion = getStringFlag('dips__un_scheduled_surgeries_api_version')

    const path = `/api/${apiVersion}/surgeries/waiting` satisfies PathsWithMethod<paths, 'post'>

    const client = getClient()
    let response

    try {
        response = await client.POST(path, params)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${getErrorMessage(path, params, error.message)}`)
        } else {
            throw new Error(`Unknown error. ${getErrorMessage(path, params, error)}`)
        }
    }

    if (!String(response.response.status).startsWith('2')) {
        throw new Error(`${getErrorMessage(path, params, response)}`)
    }

    if (!response.data) {
        console.error(`No data in ${path})`)
        return []
    }

    const authData = response.data.serviceAuthorizationResult

    // Number of items where access is denied
    const numberOfItemsWithNoAccess = authData?.numberOfItemsWithNoAccess
    // Number of items where access is permitted but is blocked because of missing access to department, ward, section or location and also all blocked patients for the user.
    const numberOfItemsBlockedForUser = authData?.numberOfItemsBlockedForUser

    if (numberOfItemsWithNoAccess || numberOfItemsBlockedForUser) {
        useStore.getState().waitingList.actions.setHasDeniedAccessItems(true)
        console.info(
            `Denied access on waiting list. Items with no access: ${numberOfItemsWithNoAccess}. Items, blocked for user: ${numberOfItemsBlockedForUser}`
        )
    } else {
        useStore.getState().waitingList.actions.setHasDeniedAccessItems(false)
    }

    const entities = response.data.surgeryWaitingList ?? []
    const transformed = transformUnScheduledSurgeries(entities)

    useStore.getState().dips.actions.addUnScheduledSurgeries(transformed)

    return transformed
}

export const importUnScheduledSurgeries = withFetchObserver(
    async (departmentKey: DepartmentKey) => fetchUnScheduledSurgeries(createPostParams(departmentKey)),
    'bookingId',
    {
        abandoned: ids => {
            useStore.getState().dips.actions.removeUnScheduledSurgeries(ids)
        },
    }
)

export const importUnScheduledSurgeriesBySpecialties = withFetchObserver(
    async (selectedSpecialties: SelectedSpecialties) => fetchUnScheduledSurgeries(createPostParamsFromSpecialties(selectedSpecialties)),
    'bookingId',
    {
        abandoned: ids => {
            useStore.getState().dips.actions.removeUnScheduledSurgeries(ids)
        },
    }
)

export const importDipsResources = async (resourceType: 'MainSurgeon' | 'OperatingTheater') => {
    const client = getClient()
    const endpoint = '/api/v2/resources' satisfies PathsWithMethod<paths, 'get'>

    const params = { params: { query: { resourceType } } }
    let response

    try {
        response = await client.GET(endpoint, params)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${getErrorMessage(endpoint, params, error.message)}`)
        } else {
            throw new Error(`Unknown error. ${getErrorMessage(endpoint, params, error)}`)
        }
    }

    if (!String(response.response.status).startsWith('2')) {
        throw new Error(`${getErrorMessage(endpoint, params, response)}`)
    }

    if (!response.data) {
        console.error(`No data in /api/v2/resources ${resourceType} response`)
        return []
    }

    const transformedDipsResources = transformDipsResources(response.data)

    useStore.getState().dips.actions.addResources(transformedDipsResources)

    return transformedDipsResources
}

export const importDipsWards = async () => {
    const client = getClient()
    const endpoint = '/api/v1/organization/wards' satisfies PathsWithMethod<paths, 'get'>
    const params = { params: { query: { includeNotAccessTo: false, includeReplaced: false } } }
    let response

    try {
        response = await client.GET(endpoint, params)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${getErrorMessage(endpoint, params, error.message)}`)
        } else {
            throw new Error(`Unknown error. ${getErrorMessage(endpoint, params, error)}`)
        }
    }

    if (!String(response.response.status).startsWith('2')) {
        throw new Error(`${getErrorMessage(endpoint, params, response)}`)
    }

    if (!response.data) {
        console.error('No data in /organization/wards response')
        return []
    }

    const wards = transformDipsWards(filterByValidity(response.data))

    useStore.getState().dips.actions.addWards(wards)

    return wards
}

export const importDipsLocalizations = async () => {
    const client = getClient()
    const endpoint = '/api/v1/organization/localizations' satisfies PathsWithMethod<paths, 'get'>
    const params = { params: { query: { includeNotAccessTo: false } } }
    let response

    try {
        response = await client.GET(endpoint, params)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${getErrorMessage(endpoint, params, error.message)}`)
        } else {
            throw new Error(`Unknown error. ${getErrorMessage(endpoint, params, error)}`)
        }
    }

    if (!String(response.response.status).startsWith('2')) {
        throw new Error(`${getErrorMessage(endpoint, params, response)}`)
    }

    if (!response.data) {
        console.error('No data in /organization/localizations response')
        return []
    }

    const localizations = transformDipsLocalizations(filterByValidity(response.data))

    useStore.getState().dips.actions.addLocalizations(localizations)

    return localizations
}

export const importDipsPostponementReasons = async () => {
    const codeListName = 'VI-UTSETTELSESKODER'

    const client = getClient()
    const endpoint = '/api/v2/code-lists/name/{codeListName}' satisfies PathsWithMethod<paths, 'get'>
    const params = {
        params: { path: { codeListName: codeListName } },
    }
    let postponementReasonsResponse

    try {
        postponementReasonsResponse = await client.GET(endpoint, params)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`${getErrorMessage(endpoint, params, error.message)}`)
        } else {
            throw new Error(`Unknown error. ${getErrorMessage(endpoint, params, error)}`)
        }
    }

    if (!String(postponementReasonsResponse.response.status).startsWith('2')) {
        throw new Error(`${getErrorMessage(endpoint, params, postponementReasonsResponse)}`)
    }

    if (!postponementReasonsResponse.data) {
        console.error('No data in /api/v2/code-lists/name/{codeListName} postponementReasons response')
        return []
    }

    const dipsPostponementReasons: DipsPostponementReason[] = postponementReasonsResponse.data.codes ?? []
    const transformedPostponementReasons = transformDipsPostponementReasons(dipsPostponementReasons)

    useStore.getState().dips.actions.addPostponementReasons(transformedPostponementReasons)

    return transformedPostponementReasons
}
